import type {
    RegisterRequest,
    LoginRequest,
    VerifyEmailRequest,
    ForgotPasswordRequest,
    ResetPasswordRequest,
    UpdatePasswordRequest,
    UpdateProfileRequest,
    AuthResponse,
    UserPublic,
    ApiResponse
} from '@arrowise/shared';
import { apiClient, ApiError } from '../utils/apiClient';

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */
export class AuthService {
    /**
     * Register a new user
     */
    async register(data: RegisterRequest): Promise<ApiResponse<{ user: UserPublic }>> {
        try {
            return await apiClient.post<{ user: UserPublic }>('/auth/register', data);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Registration failed', 500, { originalError: error });
        }
    }

    /**
     * Login user
     */
    async login(data: LoginRequest): Promise<ApiResponse<AuthResponse>> {
        try {
            // Cookies are managed by the server; just call the endpoint
            const response = await apiClient.post<AuthResponse>('/auth/login', data);
            return response;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Login failed', 500, { originalError: error });
        }
    }

    /**
     * Logout user
     */
    async logout(): Promise<ApiResponse> {
        try {
            return await apiClient.post('/auth/logout');
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Logout failed', 500, { originalError: error });
        }
    }

    /**
     * Verify email with token
     */
    async verifyEmail(data: VerifyEmailRequest): Promise<ApiResponse<{ user: UserPublic }>> {
        try {
            return await apiClient.post<{ user: UserPublic }>('/auth/verify-email', data);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Email verification failed', 500, { originalError: error });
        }
    }

    /**
     * Resend verification email
     */
    async resendVerificationEmail(): Promise<ApiResponse> {
        try {
            return await apiClient.post('/auth/resend-verification');
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Failed to resend verification email', 500, { originalError: error });
        }
    }

    /**
     * Request password reset
     */
    async forgotPassword(data: ForgotPasswordRequest): Promise<ApiResponse> {
        try {
            return await apiClient.post('/auth/forgot-password', data);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Password reset request failed', 500, { originalError: error });
        }
    }

    /**
     * Reset password with token
     */
    async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse> {
        try {
            return await apiClient.post('/auth/reset-password', data);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Password reset failed', 500, { originalError: error });
        }
    }

    /**
     * Update password (for authenticated users)
     */
    async updatePassword(data: UpdatePasswordRequest): Promise<ApiResponse> {
        try {
            return await apiClient.put('/auth/password', data);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Password update failed', 500, { originalError: error });
        }
    }

    /**
     * Update user profile
     */
    async updateProfile(data: UpdateProfileRequest): Promise<ApiResponse<{ user: UserPublic }>> {
        try {
            return await apiClient.put<{ user: UserPublic }>('/auth/profile', data);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Profile update failed', 500, { originalError: error });
        }
    }

    /**
     * Get current user profile
     */
    async getCurrentUser(): Promise<ApiResponse<{ user: UserPublic }>> {
        try {
            return await apiClient.get<{ user: UserPublic }>('/auth/me');
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Failed to fetch user profile', 500, { originalError: error });
        }
    }

    /**
     * Refresh access token
     */
    // Deprecated: cookie-based refresh happens server-side via interceptor calls; not needed on client
    async refreshToken(_refreshToken: string): Promise<ApiResponse<{ accessToken: string; refreshToken: string }>> {
        return { success: true, message: 'noop' } as any;
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        // With cookie-based auth, rely on /auth/me instead of client token knowledge
        return false;
    }

    /**
     * Get current access token (for debugging or external use)
     */
    getAccessToken(): string | null {
        return null;
    }
}

/**
 * Create and export the default auth service instance
 */
export const authService = new AuthService();

/**
 * Auth service hooks for React components
 */
export const useAuthService = () => {
    return {
        authService,
        isAuthenticated: authService.isAuthenticated(),

        // Convenience methods that can be used in components
        login: authService.login.bind(authService),
        logout: authService.logout.bind(authService),
        register: authService.register.bind(authService),
        getCurrentUser: authService.getCurrentUser.bind(authService),
        updateProfile: authService.updateProfile.bind(authService),
        updatePassword: authService.updatePassword.bind(authService),
        verifyEmail: authService.verifyEmail.bind(authService),
        resendVerificationEmail: authService.resendVerificationEmail.bind(authService),
        forgotPassword: authService.forgotPassword.bind(authService),
        resetPassword: authService.resetPassword.bind(authService),
    };
};
